import { atKeys, isEmpty, isNullish, parseString } from "@de-xima/xima-common-js-lang";
import $ from "jquery";

import { closeAllDialogs, encodeURL, getURLSessionId, getWidgetByVar } from "./Util.ts";

const LoginServlet = "/login?action=checklogin";

const defaultLoginAction: () => void = () => {
  window.location.href = encodeURL(window.location.href);
};

let OpenLoginDialogAction: (() => void) | undefined;

let actionAfterLogin = [] as (() => void)[];

function run() {
  closeAllDialogs();
  // Call custom callback, if set
  if (actionAfterLogin.length > 0) {
    try {
      for (const action of actionAfterLogin) {
        action();
      }
    } finally {
      actionAfterLogin = [];
    }
  }
  // or run the default
  else {
    defaultLoginAction();
  }
}

function runActionAfterLogin() {
  run();
}

function getParentWindow(win: Window): Window | undefined {
  try {
    const parent = win.parent;
    // Check if we can access the window, if not an error is thrown
    void parent.document;
    return parent;
  } catch {
    return undefined;
  }
}

/**
 * Called after the request completes that checks whether the user is
 * still logged in. If so, immediately runs the requested action. Otherwise,
 * waits until the user logged in.
 * @param data Data from the server.
 */
function loginCheckSuccess(data: Record<string, unknown>): void {
  if (atKeys(data, ["login"]) === true) {
    run();
  } else {
    loginCheckFailure();
  }
}

function loginCheckFailure() {
  // Show the login and wait until the user logs in
  getWidgetByVar("fcLoginDialogVar", PrimeFaces.widget.Dialog)?.show();
}

/**
 * Called when a click on the main menu was performed (the menu on the left hand side).
 * First makes a request to the server to check if we are still logged in. If we are,
 * redirect to the target page. Otherwise, the initial request will open the login dialog.
 * @param link Link that was clicked.
 * @param event Mouse event that triggered the click.
 * @returns Whether the click event should be continued.
 */
export function mainMenuOnClick(link: HTMLAnchorElement): boolean {
  // No login check required if opening in different window.
  if (link.target !== "" && link.target !== "_self") {
    return true;
  }
  // Redirect after login
  const href = link.href;
  return doWithLogin(() => {
    window.location.href = encodeURL(href);
  });
}

/**
 * If asked, first make an AJAX request to the server to check if we are still logged in.
 *
 * The given action is called once the user is logged in, ie. either immediately after the login check completes;
 * or after the user logs in on the login dialog that pops up.
 * @param action Action to be executed once the user is authorized.
 * @returns The result for the event listener to which this method was added.
 */
export function doWithLogin(action: () => void): boolean {
  // Save action
  actionAfterLogin.push(action);
  if (actionAfterLogin.length === 1) {
    $.ajax({
      dataType: "json",
      url: encodeURL(LoginServlet, true),
    })
      .then(loginCheckSuccess)
      .catch(loginCheckFailure);
  }
  // Wait for AJAX response
  return false;
}

/**
 * Callback for when the login dialog is closed.
 */
export function onHideLoginDialog(): void {
  // Do what we needed to be authorized for
  runActionAfterLogin();
}

/**
 * There is a special login view for the AJAX login dialog. It sometimes
 * happens, that when the AJAX login dialog opens and the user clicks on
 * "login", a `ViewExpiredException` occurs which causes a redirect to the
 * normal login page. That page is not usuable within the login dialog. This
 * function checks for that case and redirects the user to the AJAX login view
 * again.
 */
export function redirectToAjaxLoginIfRequired(): void {
  const parent = getParentWindow(window);
  // Check if we are within an iframe at all.
  if (isNullish(parent) || parent === window || !("Formcycle" in parent) || !("PrimeFaces" in parent)) {
    return;
  }
  if (isNullish(window.frameElement) || window.frameElement.tagName !== "IFRAME") {
    return;
  }
  // Check if the iframe is the login iframe.
  const iframe$ = atKeys(parent, ["$"]) as JQueryStatic;
  const $iframe = iframe$(window.frameElement) as JQuery;
  if (!$iframe.hasClass("fc-login-dialog-iframe")) {
    return;
  }
  // Check if we are the ajaxlogin dialog already
  if ($(".fc-ajax-login-context").length > 0) {
    return;
  }
  // Get login dialog and check if the login dialog is actually open
  // @ts-expect-error
  const dialog = parent.Formcycle.Util.getWidgetByVar("fcLoginDialogVar", parent.PrimeFaces.widget.Dialog) as
    | PrimeFaces.widget.Dialog
    | undefined;
  if (dialog === undefined || !dialog.isVisible()) {
    return;
  }
  // Get the original login dialog URL
  const src = parseString($iframe.closest(".fc-login-dialog-iframe-wrapper").data("src"));
  if (isEmpty(src)) {
    console.warn("No login dialog URL found");
    return;
  }
  // Redirect to ajax login
  window.location.href = src;
}

/**
 * Called from within the login dialog iframe after the login was successful.
 * Closes the login dialog and reloads the page.
 */
export function ajaxLoginBack(): void {
  const parent = getParentWindow(window);
  if (isNullish(parent) || parent === window) {
    console.warn("Cannot redirect, not within an iframe.");
    return;
  }
  if (!("Formcycle" in parent)) {
    console.warn("Cannot finish login, parent.Formcycle not found.");
    return;
  }
  // Transfer the new URL JSESSIONID to the parent
  const sid = getURLSessionId();
  if (sid !== undefined) {
    // @ts-expect-error
    parent.Formcycle.Util.updateURLSessionId(sid);
  }
  // Run the callback in the parent context.
  // @ts-expect-error
  parent.Formcycle.Login.onAjaxLoginSuccess();
}

/**
 * Opens the login dialog via the method set by
 * {@link setOpenLoginDialogAction}.
 */
export function openLoginDialog(): void {
  OpenLoginDialogAction?.();
}

/**
 * @param action The action that opens the login dialog when called.
 */
export function setOpenLoginDialogAction(action: () => void): void {
  OpenLoginDialogAction = action;
}
